// Casca da aplicação, nas duas formas do protótipo: barra lateral escura para
// o time interno e topo navy para o portal do cliente.

import { useEffect, useState } from 'react'
import { Link, NavLink, Outlet } from 'react-router-dom'
import { Tooltip } from 'antd'
import { LogOut, PanelLeftClose, PanelLeftOpen } from 'lucide-react'
import { useAuth } from '../../lib/auth'
import { CLIENT_NAV, initials, navForRole, ROLE_LABEL } from './navigation'
import styles from './AppLayout.module.css'

// Balão no navy da marca: ele nasce colado na barra escura e termina sobre o
// conteúdo claro, e esse tom funciona nos dois fundos. O Ant não lê variável
// CSS, então o valor de --sider-bg vem repetido aqui.
const TOOLTIP_BG = '#00073c'

// A escolha de barra reduzida é do usuário e vale para as próximas visitas.
// Mesmo tratamento do token: modo privado pode recusar, e aí segue sem gravar.
const MINI_KEY = 'c1hd.menu-reduzido'

function readMini(): boolean {
  try {
    return localStorage.getItem(MINI_KEY) === '1'
  } catch {
    return false
  }
}

function StaffSider() {
  const { user, logout } = useAuth()
  const [mini, setMini] = useState(readMini)

  useEffect(() => {
    try {
      if (mini) localStorage.setItem(MINI_KEY, '1')
      else localStorage.removeItem(MINI_KEY)
    } catch {
      /* modo privado: segue sem persistir */
    }
  }, [mini])

  if (!user) return null

  return (
    <aside className={mini ? `${styles.sider} ${styles.siderMini}` : styles.sider}>
      <div className={styles.brand}>
        <img src="/logo.png" alt="ContactOne" className={styles.logo} />
        {!mini && (
          <>
            <span className={styles.brandDivider} aria-hidden="true" />
            <span className={styles.brandText}>Central de Suporte</span>
          </>
        )}
        <Tooltip
          title={mini ? 'Expandir menu' : 'Reduzir menu'}
          placement="right"
          color={TOOLTIP_BG}
        >
          <button
            type="button"
            className={styles.toggle}
            onClick={() => setMini((v) => !v)}
            aria-pressed={mini}
            aria-label={mini ? 'Expandir menu' : 'Reduzir menu'}
          >
            {mini ? (
              <PanelLeftOpen size={18} aria-hidden="true" />
            ) : (
              <PanelLeftClose size={18} aria-hidden="true" />
            )}
          </button>
        </Tooltip>
      </div>

      <nav className={styles.nav} aria-label="Navegação principal">
        {navForRole(user.role).map((group) => (
          <div
            key={group.title}
            className={styles.group}
            role="group"
            aria-label={group.title}
          >
            {mini ? (
              <span className={styles.groupLine} aria-hidden="true" />
            ) : (
              <div className={styles.groupTitle}>{group.title}</div>
            )}
            {group.items.map((item) => {
              const link = (
                <NavLink
                  key={item.to}
                  to={item.to}
                  aria-label={mini ? item.label : undefined}
                  className={({ isActive }) => {
                    const classes = [styles.item]
                    if (mini) classes.push(styles.itemMini)
                    if (isActive) classes.push(styles.itemActive)
                    return classes.join(' ')
                  }}
                >
                  <item.icon size={18} aria-hidden="true" />
                  {!mini && item.label}
                </NavLink>
              )
              // Expandida, o nome da tela já está no item: o balão só repetiria.
              return mini ? (
                <Tooltip
                  key={item.to}
                  title={item.label}
                  placement="right"
                  color={TOOLTIP_BG}
                >
                  {link}
                </Tooltip>
              ) : (
                link
              )
            })}
          </div>
        ))}
      </nav>

      <div className={styles.me}>
        {mini ? (
          <Tooltip
            title={`${user.name}, ${ROLE_LABEL[user.role]}`}
            placement="right"
            color={TOOLTIP_BG}
          >
            <span className={styles.avatar}>{initials(user.name)}</span>
          </Tooltip>
        ) : (
          <>
            <span className={styles.avatar}>{initials(user.name)}</span>
            <div>
              <div className={styles.meName}>{user.name}</div>
              <div className={styles.meRole}>{ROLE_LABEL[user.role]}</div>
            </div>
          </>
        )}
        <Tooltip title="Sair" placement="right" color={TOOLTIP_BG}>
          <button
            type="button"
            className={styles.logout}
            onClick={() => void logout()}
            aria-label="Sair"
          >
            <LogOut size={18} aria-hidden="true" />
          </button>
        </Tooltip>
      </div>
    </aside>
  )
}

function ClientTopbar() {
  const { user, logout } = useAuth()
  if (!user) return null

  return (
    <header className={styles.topbar}>
      <div className={styles.topbarIn}>
        <Link to="/chamados" className={styles.brand} style={{ padding: 0 }}>
          <img src="/logo.png" alt="ContactOne" className={styles.logo} />
          <span className={styles.brandDivider} aria-hidden="true" />
          <span className={styles.brandText}>Central de Suporte</span>
        </Link>

        <nav className={styles.topnav} aria-label="Navegação principal">
          {CLIENT_NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/chamados'}
              className={({ isActive }) =>
                isActive ? `${styles.topItem} ${styles.topItemActive}` : styles.topItem
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className={styles.me} style={{ margin: 0, padding: 0, border: 0 }}>
          <span className={styles.avatar}>{initials(user.name)}</span>
          <div>
            <div className={styles.meName}>{user.name}</div>
            <div className={styles.meRole}>{user.companyName ?? 'Cliente'}</div>
          </div>
          <Tooltip title="Sair" placement="bottom" color={TOOLTIP_BG}>
            <button
              type="button"
              className={styles.logout}
              onClick={() => void logout()}
              aria-label="Sair"
            >
              <LogOut size={18} aria-hidden="true" />
            </button>
          </Tooltip>
        </div>
      </div>
    </header>
  )
}

export function AppLayout() {
  const { user } = useAuth()

  if (user?.role === 'cliente') {
    return (
      <div className={styles.column}>
        <ClientTopbar />
        <main className={styles.main}>
          <div className={styles.content}>
            <Outlet />
          </div>
        </main>
      </div>
    )
  }

  return (
    <div className={styles.shell}>
      <StaffSider />
      <main className={styles.main}>
        <div className={styles.content}>
          <Outlet />
        </div>
      </main>
    </div>
  )
}
